import jsPDF from 'jspdf';
import { renderFacilityInformation } from '../../../../../../../utils/pdfHelpers';

/**
 * Generates a single PDF file that contains **all** invoices passed in the array.
 * For every invoice the exact same layout that is used by `generateServiceInvoicePDF` is
 * rendered on a dedicated page.
 *
 * Because `generateServiceInvoicePDF` internally creates / saves its own jsPDF instance,
 * we cannot reuse it directly. Instead, this helper re-implements the layout logic but
 * targets a **shared** jsPDF instance so we end up with one consolidated file.
 *
 * If you ever update the styling of `DownloadServiceInvoice.js`, please replicate the
 * changes here as well, or – even better – refactor both files to share a common
 * rendering helper.
 *
 * @param {Array<Object>} invoicesArr   – Array of service-invoice records chosen by the user.
 * @param {Object|null} patientInfo     – Basic patient demographics (may be null).
 * @param {Object|null} officeData      – Facility profile / clinic information (may be null).
 */
export const generateMultipleServiceInvoicesPDF = async (
  invoicesArr = [],
  patientInfo = null,
  officeData = null
) => {
  if (!Array.isArray(invoicesArr) || invoicesArr.length === 0) {
    // Nothing to do – silently exit.
    return;
  }

  // We render every invoice on its own page.
  const pdf = new jsPDF('p', 'mm', 'letter');

  // Helper that copies the rendering logic of DownloadServiceInvoice.js but targets
  // the provided `pdf` instance instead of creating a new one.
  const appendInvoiceToPDF = (invoiceData) => {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const lineHeight = 7;
    let yPosition = margin;

    /* ----------------------------- Header ----------------------------- */
    pdf.setFontSize(24);
    pdf.setTextColor(41, 128, 185);
    pdf.text('SERVICE INVOICE', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += lineHeight * 2.5;

    /* ------------------ Two-column: Facility | Patient ----------------- */
    const leftColumnX = margin;
    const rightColumnX = pageWidth / 2 + 10;
    const columnWidth = (pageWidth - margin * 2 - 20) / 2;
    const sectionStartY = yPosition;

    /* Facility Info */
    const facilityEndY = renderFacilityInformation(pdf, officeData, {
      startX: leftColumnX,
      startY: yPosition,
      lineHeight,
      columnWidth,
    });
    yPosition = Math.max(yPosition, facilityEndY);

    /* Patient Info */
    if (patientInfo) {
      let patientY = sectionStartY;

      pdf.setFontSize(12);
      pdf.setTextColor(52, 73, 94);
      pdf.text('Patient Information', rightColumnX, patientY);
      patientY += lineHeight;

      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);

      const patientName = patientInfo.first_name && patientInfo.last_name
        ? `${patientInfo.first_name} ${patientInfo.middle_name ? patientInfo.middle_name + ' ' : ''}${patientInfo.last_name}`
        : 'N/A';
      pdf.text(`Name: ${patientName}`, rightColumnX, patientY);
      patientY += lineHeight * 0.8;

      if (patientInfo.date_of_birth) {
        const dob = new Date(patientInfo.date_of_birth).toLocaleDateString();
        pdf.text(`DOB: ${dob}`, rightColumnX, patientY);
        patientY += lineHeight * 0.8;

        const today = new Date();
        const age = Math.floor((today - new Date(patientInfo.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000));
        pdf.text(`Age: ${age} y/o`, rightColumnX, patientY);
        patientY += lineHeight * 0.8;
      }

      if (patientInfo.gender) {
        pdf.text(`Gender: ${patientInfo.gender}`, rightColumnX, patientY);
        patientY += lineHeight * 0.8;
      }

      if (patientInfo.id) {
        pdf.text(`Patient ID: ${patientInfo.id}`, rightColumnX, patientY);
        patientY += lineHeight * 0.8;
      }

      const addressParts = [];
      if (patientInfo.current_address_line_1) addressParts.push(patientInfo.current_address_line_1);
      if (patientInfo.current_address_line_2) addressParts.push(patientInfo.current_address_line_2);
      if (patientInfo.current_city) addressParts.push(patientInfo.current_city);
      if (patientInfo.current_state) addressParts.push(patientInfo.current_state);
      if (patientInfo.current_country) addressParts.push(patientInfo.current_country);
      if (patientInfo.current_zipcode) addressParts.push(patientInfo.current_zipcode);

      if (addressParts.length > 0) {
        const addrLines = pdf.splitTextToSize(`Address: ${addressParts.join(', ')}`, columnWidth);
        addrLines.forEach((l) => {
          pdf.text(l, rightColumnX, patientY);
          patientY += lineHeight * 0.8;
        });
      }

      if (patientInfo.patient_phone) {
        pdf.text(`Phone: ${patientInfo.patient_phone}`, rightColumnX, patientY);
        patientY += lineHeight * 0.8;
      }
      if (patientInfo.patient_email) {
        pdf.text(`Email: ${patientInfo.patient_email}`, rightColumnX, patientY);
        patientY += lineHeight * 0.8;
      }

      if (invoiceData) {
        patientY += lineHeight * 0.5;
        pdf.setDrawColor(220, 220, 220);
        pdf.line(rightColumnX, patientY, rightColumnX + columnWidth, patientY);
        patientY += lineHeight;

        pdf.setFontSize(10);
        pdf.setTextColor(52, 73, 94);
        pdf.text('Invoice Information', rightColumnX, patientY);
        patientY += lineHeight;

        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);

        if (invoiceData.invoice_id) {
          pdf.text(`Invoice ID: ${invoiceData.invoice_id}`, rightColumnX, patientY);
          patientY += lineHeight * 0.8;
        }
        if (invoiceData.status) {
          const statusText = invoiceData.status.charAt(0).toUpperCase() + invoiceData.status.slice(1);
          pdf.text(`Status: ${statusText}`, rightColumnX, patientY);
          patientY += lineHeight * 0.8;
        }
        if (invoiceData.issue_date) {
          const issueDate = new Date(invoiceData.issue_date).toLocaleDateString();
          const issueTime = new Date(invoiceData.issue_date).toLocaleTimeString();
          pdf.text(`Issue Date: ${issueDate} ${issueTime}`, rightColumnX, patientY);
          patientY += lineHeight * 0.8;
        }
        if (invoiceData.created_by || invoiceData.added_by) {
          pdf.text(`Created By: ${invoiceData.created_by || invoiceData.added_by}`, rightColumnX, patientY);
          patientY += lineHeight * 0.8;
        }
        if (invoiceData.payment_method) {
          pdf.text(`Payment Method: ${invoiceData.payment_method}`, rightColumnX, patientY);
          patientY += lineHeight * 0.8;
        }
      }
      yPosition = Math.max(yPosition, patientY);
    }

    yPosition += lineHeight * 1.5;
    pdf.setDrawColor(52, 73, 94);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += lineHeight * 1.5;

    /* ------------------------- Service Table ------------------------- */
    if (invoiceData) {
      if (yPosition > pageHeight - 90) {
        pdf.addPage();
        yPosition = margin;
      }

      // Currency settings - use office settings if available, fallback to invoice data or default
      const currency = officeData?.local_currency || invoiceData.currency || 'USD';
      const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'PKR' ? 'PKR ' : currency === 'GBP' ? '£' : currency === 'JPY' ? '¥' : currency === 'CAD' ? 'C$' : currency === 'AUD' ? 'A$' : `${currency} `;

      pdf.setFontSize(12);
      pdf.setTextColor(52, 73, 94);
      pdf.text('Services:', margin, yPosition);
      yPosition += lineHeight;

      const servicesArray = Array.isArray(invoiceData) ? invoiceData : [invoiceData];
      const tableStartX = margin;
      const colPos = {
        number: tableStartX + 5,
        service: tableStartX + 25,
        qty: tableStartX + 95,
        unitPrice: tableStartX + 120,
        totalPrice: tableStartX + 160,
      };

      pdf.setFontSize(10);
      pdf.setTextColor(255, 255, 255);
      pdf.setFillColor(52, 73, 94);
      pdf.rect(margin, yPosition, pageWidth - margin * 2, lineHeight + 2, 'F');
      pdf.text('#', colPos.number, yPosition + lineHeight * 0.7);
      pdf.text('Service', colPos.service, yPosition + lineHeight * 0.7);
      pdf.text('QTY', colPos.qty, yPosition + lineHeight * 0.7);
      pdf.text('Unit Price', colPos.unitPrice, yPosition + lineHeight * 0.7);
      pdf.text('Total Price', colPos.totalPrice, yPosition + lineHeight * 0.7);
      yPosition += lineHeight + 2;
      pdf.setTextColor(0, 0, 0);

      let totalAmount = 0;

      servicesArray.forEach((service, idx) => {
        if (yPosition > pageHeight - 80) {
          pdf.addPage();
          yPosition = margin;
          pdf.setFontSize(10);
          pdf.setTextColor(255, 255, 255);
          pdf.setFillColor(52, 73, 94);
          pdf.rect(margin, yPosition, pageWidth - margin * 2, lineHeight + 2, 'F');
          pdf.text('#', colPos.number, yPosition + lineHeight * 0.7);
          pdf.text('Service', colPos.service, yPosition + lineHeight * 0.7);
          pdf.text('QTY', colPos.qty, yPosition + lineHeight * 0.7);
          pdf.text('Unit Price', colPos.unitPrice, yPosition + lineHeight * 0.7);
          pdf.text('Total Price', colPos.totalPrice, yPosition + lineHeight * 0.7);
          yPosition += lineHeight + 2;
          pdf.setTextColor(0, 0, 0);
        }

        if (idx % 2 === 1) {
          pdf.setFillColor(248, 249, 250);
          pdf.rect(margin, yPosition, pageWidth - margin * 2, lineHeight + 2, 'F');
        }

        pdf.setFontSize(9);
        pdf.text(`${idx + 1}.`, colPos.number, yPosition + lineHeight * 0.7);

        const serviceName = service.service_name || 'Unknown';
        const maxServiceWidth = colPos.qty - colPos.service - 5;
        const svcLines = pdf.splitTextToSize(serviceName, maxServiceWidth);
        const rowHeight = Math.max(lineHeight + 2, svcLines.length * lineHeight);
        svcLines.forEach((l, i) => {
          pdf.text(l, colPos.service, yPosition + lineHeight * 0.7 + i * lineHeight);
        });

        const qty = service.quantity || 1;
        pdf.text(qty.toString(), colPos.qty + 5, yPosition + lineHeight * 0.7);

        const unitPrice = parseFloat(service.price) || 0;
        pdf.text(`${currencySymbol}${unitPrice.toFixed(2)}`, colPos.unitPrice, yPosition + lineHeight * 0.7);

        const totalPrice = unitPrice * qty;
        totalAmount += totalPrice;
        pdf.text(`${currencySymbol}${totalPrice.toFixed(2)}`, colPos.totalPrice, yPosition + lineHeight * 0.7);

        yPosition += rowHeight + 1;
      });

      /* Financial Summary */
      yPosition += lineHeight * 1.5;
      pdf.setDrawColor(220, 220, 220);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += lineHeight * 1.5;

      const subtotal = invoiceData.sub_total === 0 || typeof invoiceData.sub_total === 'undefined'
        ? (invoiceData.price ? parseFloat(invoiceData.price) : totalAmount)
        : parseFloat(invoiceData.sub_total);

      const discountPercentage = invoiceData.discount ? parseFloat(invoiceData.discount) : null;
      const discountAmount = invoiceData.discounted_amount ? parseFloat(invoiceData.discounted_amount) : null;
      const taxAmount = invoiceData.tax ? parseFloat(invoiceData.tax) : null;
      const grandTotal = invoiceData.grand_total_amount
        ? parseFloat(invoiceData.grand_total_amount)
        : subtotal - (discountAmount || 0) + (taxAmount || 0);

      const summaryLabelX = pageWidth - margin - 120;
      const summaryValueX = pageWidth - margin - 10;

      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Sub Total:', summaryLabelX, yPosition);
      pdf.text(`${currencySymbol}${subtotal.toFixed(2)}`, summaryValueX, yPosition, { align: 'right' });
      yPosition += lineHeight * 1.2;

      if (discountAmount && discountAmount > 0) {
        pdf.setTextColor(220, 53, 69);
        const discountLabel = discountPercentage ? `Discount ${discountPercentage}%:` : 'Discount:';
        pdf.text(discountLabel, summaryLabelX, yPosition);
        pdf.text(`${currencySymbol}${discountAmount.toFixed(2)}`, summaryValueX, yPosition, { align: 'right' });
        pdf.setTextColor(0, 0, 0);
        yPosition += lineHeight * 1.2;
      }

      if (taxAmount && taxAmount > 0) {
        pdf.text('Tax:', summaryLabelX, yPosition);
        pdf.text(`${currencySymbol}${taxAmount.toFixed(2)}`, summaryValueX, yPosition, { align: 'right' });
        yPosition += lineHeight * 1.2;
      }

      yPosition += lineHeight * 0.5;
      pdf.setDrawColor(52, 73, 94);
      pdf.setLineWidth(0.5);
      pdf.line(summaryLabelX, yPosition, summaryValueX, yPosition);
      yPosition += lineHeight * 1.2;

      pdf.setFontSize(14);
      pdf.setTextColor(52, 73, 94);
      pdf.text('Grand Total:', summaryLabelX, yPosition);
      pdf.text(`${currencySymbol}${grandTotal.toFixed(2)}`, summaryValueX, yPosition, { align: 'right' });
      yPosition += lineHeight * 2;
    }
  };

  // Iterate over invoices and render each.
  invoicesArr.forEach((inv, idx) => {
    if (idx !== 0) {
      pdf.addPage();
    }
    appendInvoiceToPDF(inv);
  });

  /* -------------------- Pagination footer -------------------- */
  const pageCount = pdf.internal.pages.length - 1;
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text(`Page ${i} of ${pageCount}`, pdf.internal.pageSize.getWidth() - 20, pdf.internal.pageSize.getHeight() - 10, { align: 'right' });
  }

  /* -------------------------- Save --------------------------- */
  const dateStamp = new Date().toISOString().split('T')[0];
  const patientNameForFile = patientInfo?.first_name && patientInfo?.last_name ? `${patientInfo.first_name}_${patientInfo.last_name}` : 'Patient';
  const fileName = `Service_Invoices_${patientNameForFile}_${dateStamp}.pdf`;
  pdf.save(fileName);
};
